import React, { useEffect, useState } from "react";
import { Prompt } from "react-router-dom";
import { TextField, Switch, Box, Grid, Backdrop, Button } from "@material-ui/core";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { makeStyles, Theme } from "@material-ui/core/styles";
import EditIcon from "@material-ui/icons/Edit";
import { useFormik } from "formik";
import { object, string } from "yup";
import { useAppDispatch } from "src/app-redux/hooks";
import { IVehicleType } from "src/common/typings/vehicle";
import NewCommonButton from "src/components/CommonButton/NewCommonButton";
import CommonAutoCompleteField from "../../../../../../components/CommonAutoCompleteField/CommonAutoCompleteField";
import CommonButton from "../../../../../../components/CommonButton/CommonButton";
import { columns } from "./columns";
import { setisDirtyValue } from "src/app-redux/configuration/configurationSlice";
import { supplierTypeList } from "src/common/utils/app.dropdowns";
import { Check, Close } from "@material-ui/icons";
import { Tooltip } from "@mui/material";
import CommonMaterialGrid from "src/components/CommonXGrid/CommonMaterialGrid";
import InputMask from "react-input-mask";
import CommonLoader from "src/components/Loader/CommonLoader";

const useStyles = makeStyles((theme: Theme) => ({
  formRow: {
    marginBottom: "0px",
  },
  backdrop: {
    zIndex: theme.zIndex.modal + 1,
    color: "#fff",
  },
  tabCardContent: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    overflow: "auto",
  },

  UserMainPanel: {
    width: "100%",
    float: "left",
    padding: "0px 20px",
    paddingRight: 0,
  },
  mainConainer: { marginTop: "0px" },
}));

export interface IForm extends IVehicleType {}

interface Props {
  // In TS we make an interface of Props to shape them.
  handleSubmit: (formData: IForm) => Promise<void>;
  updateSubmit: (formData: IForm) => Promise<void>;
  initialValues?: IVehicleType; // '?' Its means initialValues is Optional
  loading?: any;

  tableRecord?: any;
  temp?: any;
}

export function InputFields({
  initialValues,
  handleSubmit,
  updateSubmit,
  loading,
  tableRecord,
}: Props): React.ReactElement {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const [isEditMode, setisEditMode] = React.useState(false);
  const terminalType = Number(localStorage.getItem("terminalType"));

  const [submitCounter, setSubmitCounter] = useState<any>(0);
  const [displayColumns, setDisplayColumns] = React.useState<any>(columns);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);

  const validationSchema = object({
    supplierName: string()
      .required("This field is required")
      .matches(/^[a-zA-Z ]*$/, "Only alphabets are allowed for this field ")
      .max(50, "Maximum 50 characters are allowed."),
    licenseNumber:
      terminalType === 4
        ? string().max(50, "Maximum 25 characters are allowed.")
        : string().required("This field is required").max(50, "Maximum 25 characters are allowed."),
    addressDetails:
      terminalType === 4
        ? string().max(50, "Maximum 50 characters are allowed.")
        : string().required("This field is required").max(50, "Maximum 50 characters are allowed."),
    supplierType: string().required("This field is required"),
    cnicNumber:
      terminalType === 4
        ? string()
            .max(25, "Maximum 25 characters are allowed")
            .matches(/^[A-Za-z0-9-,_ ]*$/, "Only alphanumeric are allowed for this field ")
        : string()
            .required("This field is required")
            .max(25, "Maximum 25 characters are allowed")
            .matches(/^[A-Za-z0-9-,_ ]*$/, "Only alphanumeric are allowed for this field "),
    mobileNumber:
      terminalType === 4
        ? string()
            .max(20, "Maximum 12 characters allowed")
            .min(1, "min 1 characters allowed")
            .test("len", "Invalid Phone Number No.", (val = "") => {
              const val_length_without_dashes = val.replace(/-|_/g, "").length;
              return val_length_without_dashes === 13 || val_length_without_dashes === 0;
            })
        : string()
            .required("This field is required")
            .max(20, "Maximum 12 characters allowed")
            .min(1, "min 1 characters allowed")
            .test("len", "Invalid Phone Number No.", (val = "") => {
              const val_length_without_dashes = val.replace(/-|_/g, "").length;
              return val_length_without_dashes === 13 || val_length_without_dashes === 0;
            }),
  });
  const formik = useFormik<IForm>({
    enableReinitialize: true,
    initialValues: {
      id: initialValues?.id || 0,
      isActive: initialValues?.isActive || Boolean(false),
      supplierType: initialValues?.supplierType || "",
      cnicNumber: initialValues?.cnicNumber || "",
      supplierName: initialValues?.supplierName || "",
      mobileNumber: initialValues?.mobileNumber || "",
      addressDetails: initialValues?.addressDetails || "",
      licenseNumber: initialValues?.licenseNumber || "",
    },
    validationSchema: validationSchema,
    onSubmit: (values, actions) => {
      if (isEditMode) updateSubmit(values);
      else {
        formik.setFieldValue("id", 0);
        handleSubmit(values);
      }
      actions.setSubmitting(false);
      actions.resetForm();
      setisEditMode(false);
      setIsDirtyValue(false);
      setSelectedRecord(null);
    },
  });

  const handleUpdateClick = (params: any) => {
    setSelectedRecord(params);
    formik.setFieldValue("supplierType", params?.supplierType);
    formik.setFieldValue("cnicNumber", params?.cnicNumber);
    formik.setFieldValue("supplierName", params?.supplierName);
    formik.setFieldValue("mobileNumber", params?.mobileNumber);
    formik.setFieldValue("addressDetails", params?.addressDetails);
    formik.setFieldValue("licenseNumber", params?.licenseNumber);
    formik.setFieldValue("id", params?.id);
    formik.setFieldValue("isActive", params?.isActive);
    setisEditMode(true);
  };
  useEffect(() => {
    if (submitCounter) formik.submitForm();
  }, [submitCounter]);

  /* GRID Code */
  useEffect(() => {
    if (tableRecord.length) {
      const transformedColumns: any = columns.map((column: any) =>
        column.accessorKey === "isActive"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return (
                  <React.Fragment>
                    <Box>
                      <Tooltip title={row.original?.isActive ? "Yes" : "No"}>
                        <Button style={{ minWidth: "30px", padding: "4px", color: "gray" }}>
                          {row.original?.isActive ? <Check /> : <Close />}
                        </Button>
                      </Tooltip>
                    </Box>
                  </React.Fragment>
                );
              },
            }
          : column.accessorKey === "actions"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return (
                  <>
                    <NewCommonButton
                      onClick={() => {
                        setIsDirtyValue(true);
                        handleUpdateClick(row.original);
                      }}
                      icon={<EditIcon />}
                      isEdit={true}
                    >
                      Edit
                    </NewCommonButton>
                  </>
                );
              },
            }
          : column
      );

      setDisplayColumns(transformedColumns);
    }
  }, [tableRecord]);

  // Show prompt if the form is fully or partially filled and user wants to navigate away
  const [isDirty, setIsDirty] = React.useState<boolean>(false);
  useEffect(() => {
    // If prompt, when reloading or closing the tab
    window.onbeforeunload = function (e: Event) {
      if (isDirty) {
        e.preventDefault();
        return "Changes you made may not be saved.";
      }
    };
  }, [isDirty]);
  function handleValueChange(e: any) {
    formik.handleChange(e);
    e.target.value ? setIsDirtyValue(true) : setIsDirtyValue(false);
  }

  function setIsDirtyValue(val: boolean) {
    setIsDirty(val);
    dispatch(setisDirtyValue(val));
  }
  return (
    <>
      {/* If prompt, when navigating to another page inside the app */}
      <Prompt when={isDirty} message="Changes you made may not be saved." />
      <form onSubmit={formik.handleSubmit}>
        <Backdrop className={classes.backdrop} open={loading}>
          <CommonLoader />
        </Backdrop>
        <Grid container direction="row" alignItems="flex-start">
          {/* First Section */}
          <>
            <Grid className={classes.UserMainPanel}>
              <Grid
                // Container
                container
                className={classes.mainConainer}
                direction="row"
                alignItems="center"
                spacing={2}
              >
                <Grid container item xs={12} md={12} lg={12} spacing={2}>
                  <Grid item xs={12} md={4} lg={3} style={{ paddingLeft: "0px" }}>
                    <Box>
                      <CommonAutoCompleteField
                        cssClassName="customTextField"
                        disabled={false}
                        label="Supplier Type"
                        placeholder="Select Type"
                        name="supplierType"
                        optionsType={supplierTypeList}
                        getOptionLabel={(option) => option.name}
                        renderOption={(option) => (
                          <>
                            <span>{option.name}</span>
                          </>
                        )}
                        required
                        value={
                          supplierTypeList.find(
                            (st: any) => st.name === formik.values.supplierType
                          ) || null
                        }
                        onChange={(option: any) => {
                          option && option !== ""
                            ? formik.setFieldValue("supplierType", option.name)
                            : "";
                        }}
                        error={formik.touched.supplierType && Boolean(formik.errors.supplierType)}
                        helperText={formik.touched.supplierType && formik.errors.supplierType}
                      />
                    </Box>
                  </Grid>
                  {terminalType !== 4 && (
                    <Grid item xs={12} md={4} lg={3} style={{ paddingLeft: "0px" }}>
                      <Box>
                        <TextField
                          size="small"
                          fullWidth
                          required={terminalType !== 4}
                          className="customTextField"
                          variant="outlined"
                          InputLabelProps={{
                            shrink: true,
                          }}
                          label="CNIC Number"
                          name="cnicNumber"
                          id="driverCNIC"
                          placeholder="Enter CNIC Number"
                          disabled={false}
                          value={formik.values.cnicNumber}
                          onChange={handleValueChange}
                          onPaste={(e: any) => {
                            if (/[^A-Za-z0-9-,_ ]/.test(e.clipboardData.getData("text")))
                              e.preventDefault();
                          }}
                          onKeyDownCapture={(e: any) => {
                            if (/[^A-Za-z0-9-,_ ]/.test(e.key)) e.preventDefault();
                          }}
                          error={formik.touched.cnicNumber && Boolean(formik.errors.cnicNumber)}
                          helperText={formik.touched.cnicNumber && formik.errors.cnicNumber}
                        />
                      </Box>
                    </Grid>
                  )}
                  <Grid item xs={12} md={4} lg={3} style={{ paddingLeft: "0px" }}>
                    <Box>
                      <TextField
                        className="customTextField"
                        required
                        size="small"
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="Company Name"
                        name="supplierName"
                        placeholder="Enter Company Name"
                        disabled={false}
                        value={formik.values.supplierName}
                        onChange={handleValueChange}
                        error={formik.touched.supplierName && Boolean(formik.errors.supplierName)}
                        helperText={formik.touched.supplierName && formik.errors.supplierName}
                      />
                    </Box>
                  </Grid>
                  {terminalType !== 4 && (
                    <>
                      <Grid item xs={12} md={4} lg={3} style={{ paddingLeft: "0px" }}>
                        <Box>
                          <InputMask
                            mask="+99-999-999-9999"
                            value={formik.values.mobileNumber}
                            disabled={false}
                            onChange={handleValueChange}
                          >
                            {() => (
                              <TextField
                                size="small"
                                fullWidth
                                required={terminalType !== 4}
                                className="customTextField"
                                variant="outlined"
                                InputLabelProps={{
                                  shrink: true,
                                }}
                                label="Mobile Number"
                                name="mobileNumber"
                                id="mobileNumber"
                                placeholder="+92-123-456-7890"
                                type="text"
                                error={
                                  formik.touched.mobileNumber && Boolean(formik.errors.mobileNumber)
                                }
                                helperText={
                                  formik.touched.mobileNumber && formik.errors.mobileNumber
                                }
                              />
                            )}
                          </InputMask>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={4} lg={3} style={{ paddingLeft: "0px" }}>
                        <Box>
                          <TextField
                            className="customTextField"
                            required={terminalType !== 4}
                            size="small"
                            fullWidth
                            variant="outlined"
                            InputLabelProps={{
                              shrink: true,
                            }}
                            label="Address"
                            name="addressDetails"
                            placeholder="Enter Address Detail"
                            disabled={false}
                            value={formik.values.addressDetails}
                            onChange={handleValueChange}
                            error={
                              formik.touched.addressDetails && Boolean(formik.errors.addressDetails)
                            }
                            helperText={
                              formik.touched.addressDetails && formik.errors.addressDetails
                            }
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={4} lg={3} style={{ paddingLeft: "0px" }}>
                        <Box>
                          <TextField
                            className="customTextField"
                            required={terminalType !== 4}
                            size="small"
                            fullWidth
                            variant="outlined"
                            InputLabelProps={{
                              shrink: true,
                            }}
                            label="License #"
                            name="licenseNumber"
                            placeholder="Enter License Number"
                            disabled={false}
                            value={formik.values.licenseNumber}
                            onChange={handleValueChange}
                            error={
                              formik.touched.licenseNumber && Boolean(formik.errors.licenseNumber)
                            }
                            helperText={formik.touched.licenseNumber && formik.errors.licenseNumber}
                          />
                        </Box>
                      </Grid>
                    </>
                  )}
                  <Grid item xs={6} md={2} lg={1}>
                    <Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(formik.values.isActive)}
                            onChange={formik.handleChange}
                            name="isActive"
                            color="primary"
                          />
                        }
                        label="Active"
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={6} md={2} lg={2}>
                    <Box mb={2} mt={1} style={{ float: "right" }}>
                      <CommonButton
                        isGreen
                        cssClass="customSubmitBtn"
                        disable={
                          selectedRecord
                            ? formik.values.addressDetails === selectedRecord.addressDetails &&
                              formik.values.cnicNumber === selectedRecord.cnicNumber &&
                              formik.values.licenseNumber === selectedRecord.licenseNumber &&
                              formik.values.mobileNumber === selectedRecord.mobileNumber &&
                              formik.values.supplierName === selectedRecord.supplierName &&
                              formik.values.supplierType === selectedRecord.supplierType &&
                              formik.values.isActive === selectedRecord.isActive
                            : JSON.stringify(formik.values) === JSON.stringify(formik.initialValues)
                        }
                        onClick={() => setSubmitCounter((prev: any) => prev + 1)}
                      >
                        {isEditMode ? "Update" : "Add"}
                      </CommonButton>
                    </Box>
                  </Grid>
                </Grid>
                {/* End Inner Container */}
              </Grid>
              {/* Outer Container */}
            </Grid>
          </>
        </Grid>
      </form>
      <CommonMaterialGrid
        defaultPerPageRecord={10}
        height={400}
        loader={false}
        rows={tableRecord || []}
        columns={displayColumns}
      />
    </>
  );
}
